import { ReactElement } from 'react';

import { GetStaticProps } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

import { HiddenSeoHeading } from '@/components/functional/HiddenSEOHeading/HiddenSEOHeading';
import { DefaultLayout, Footer } from '@/components/layouts';
import { LetsBuildSomethingSection } from '@/components/sections/common';
import { OurGuidesSection } from '@/components/sections/library/OurGuidesSection';
import { LibraryFragment } from '@/components/sections/library/typings';
import { PropsBuilder } from '@/graphql/api';
import {
  useGetGlobalQuery,
  useGetLibrariesPageQuery,
  useGetLibrariesQuery,
  useGetMenuQuery,
} from '@/graphql/generated';
import { useLocale } from '@/hooks';
import { NextPageWithLayout } from '@/pages/_app';
import { DEFAULT_LOCALE, LOCALES, PAGE_REVALIDATE_TIME } from '@/utils/constants';

const Library: NextPageWithLayout = () => {
  const locale = useLocale();
  const ebooks = useGetLibrariesQuery({ locale }).data?.libraries?.data;

  return (
    <>
      <HiddenSeoHeading heading="Our guides" />
      <OurGuidesSection ebooks={ebooks?.map((e) => e.attributes) as LibraryFragment[]} />
      <LetsBuildSomethingSection overflow={false} />
    </>
  );
};

Library.getLayout = (page: ReactElement) => {
  const { metadata, menu } = page.props;

  return (
    <DefaultLayout metadata={metadata} menu={menu}>
      {page}
      <Footer menu={menu} />
    </DefaultLayout>
  );
};

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const propsBuilder = new PropsBuilder();

  propsBuilder.withQuery(useGetGlobalQuery, {
    variables: {
      locale,
    },
  });
  propsBuilder.withQuery(useGetLibrariesQuery, {
    variables: { locale },
  });
  propsBuilder.withQuery(useGetLibrariesPageQuery, {
    getMetadata: (data) => data.guidesPage?.data?.attributes?.metadata,
  });
  propsBuilder.withQuery(useGetMenuQuery, {
    getMenu: (data) => data.menu,
  });

  const lang = await serverSideTranslations(locale || DEFAULT_LOCALE, [LOCALES.COMMON]);

  return {
    props: {
      ...(await propsBuilder.build(lang)),
    },
    revalidate: PAGE_REVALIDATE_TIME,
  };
};

export default Library;
